import { PUI, RegistryId } from '@pleased-npm/fe-customisation';
import { InteractionView } from '@/stores/useChatWidget';
import { MockedFlowActions } from './types';
import {
  getMockedMessagesByRegistryId,
  createMockedResumableChat,
  createMockedVerifyChatOpen,
  createMockedSearch,
  createMockedArticle,
  createMockedConversation,
  mockGuestUser,
  createMockedRecentResolvedTicket,
  createMockedRelatedArticles,
  createMockedOpenConversations,
  createMockedClosedConversations,
  createMockedFeedbackSelections,
  createMockedSelectedImprovements,
  createMockedConnectData,
} from './mocks';

/**
 * Maps registry IDs to their corresponding flow actions.
 * This determines what happens when the chatbot starts from a specific page.
 */
export const REGISTRY_FLOW_MAP: Record<RegistryId, MockedFlowActions> = {
  [PUI.REGISTRY_IDS.HOME]: {
    navigateTo: '/new-home',
    showChatOverlay: false,
    skipDefaultFlow: true,
    getMockData: () => ({
      recentResolvedTicket: createMockedRecentResolvedTicket(),
      mockedArticle: createMockedArticle(),
    }),
  },
  [PUI.REGISTRY_IDS.HELP_PAGE]: {
    navigateTo: '/new-help',
    skipDefaultFlow: true,
    getMockData: () => ({
      mockedArticle: createMockedArticle(),
    }),
  },
  [PUI.REGISTRY_IDS.ARTICLE]: {
    navigateTo: '/new-article/123',
    skipDefaultFlow: true,
    getMockData: () => ({
      mockedArticle: createMockedArticle(),
      relatedArticles: createMockedRelatedArticles(),
    }),
  },
  [PUI.REGISTRY_IDS.ARTICLE_SEARCH]: {
    navigateTo: '/new-help',
    skipDefaultFlow: true,
    getMockData: () => ({
      searchState: createMockedSearch(),
    }),
  },
  [PUI.REGISTRY_IDS.CHATBOT]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    skipDefaultFlow: true,
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
    }),
  },
  [PUI.REGISTRY_IDS.CONVERSATIONS_PAGE]: {
    navigateTo: '/conversations',
    skipDefaultFlow: true,
    getMockData: () => ({
      openConversations: createMockedOpenConversations(),
      closedConversations: createMockedClosedConversations(),
    }),
  },
  [PUI.REGISTRY_IDS.CSAT]: {
    showChatOverlay: true,
    interactionView: InteractionView.CSAT,
    skipDefaultFlow: true,
    navigateTo: '/new-home',
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
      feedbackSelections: createMockedFeedbackSelections(),
      selectedImprovements: createMockedSelectedImprovements(),
    }),
  },
  [PUI.REGISTRY_IDS.CHATBOT_OPEN_REQUEST]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    interactionView: InteractionView.OPEN_REQUEST_DESCRIPTION,
    skipDefaultFlow: true,
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
      connectData: createMockedConnectData(),
    }),
  },
  [PUI.REGISTRY_IDS.CHATBOT_TRANSACTIONS]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    skipDefaultFlow: true,
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
    }),
  },
  [PUI.REGISTRY_IDS.CHATBOT_LIVE_CHAT]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    interactionView: InteractionView.LIVE_CHAT_INDICATOR,
    skipDefaultFlow: true,
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
      connectData: createMockedConnectData(),
    }),
  },
  [PUI.REGISTRY_IDS.CHATBOT_CALL]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    interactionView: InteractionView.VOICE_CHAT_INDICATOR,
    skipDefaultFlow: true,
    getMockData: (registryId) => ({
      messages: getMockedMessagesByRegistryId(registryId),
      scrollToBottom: true,
      connectData: createMockedConnectData(),
    }),
  },
  [PUI.REGISTRY_IDS.CONVERSATION_DETAILS_PAGE]: {
    navigateTo: '/conversation/123?status=RESOLVED',
    skipDefaultFlow: true,
    getMockData: () => ({
      mockedConversation: createMockedConversation({ minimal: true }),
    }),
  },
  [PUI.REGISTRY_IDS.GUEST_FORM_PAGE]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    interactionView: InteractionView.GUEST_FORM,
    skipDefaultFlow: true,
    setupUser: mockGuestUser,
  },
  [PUI.REGISTRY_IDS.LOGIN_PAGE]: {
    navigateTo: '/new-home',
    showChatOverlay: true,
    interactionView: InteractionView.LOGIN,
    skipDefaultFlow: true,
    setupUser: mockGuestUser,
  },
  [PUI.REGISTRY_IDS.MODAL]: {
    navigateTo: '/new-home',
    skipDefaultFlow: true,
    getMockData: () => ({
      resumableChat: createMockedResumableChat(),
      verifyChatOpen: createMockedVerifyChatOpen(),
    }),
  },
  // Nothing to do for default theme
  [PUI.REGISTRY_IDS.DEFAULT_THEME]: {
    skipDefaultFlow: false,
  },
};
